<template>
  <b-modal
    id="handover-service-system-modal"
    cancel-variant="outline-secondary"
    :ok-title="$t('button.confirm')"
    :cancel-title="$t('button.close')"
    centered
    size="lg"
    no-close-on-backdrop
    :title="$t('button.handover')"
    :loading="loadingSubmit"
    @ok="handleOk"
    @cancel="handleClose"
    @close="handleClose"
  >
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        class="btn"
        @click="handleClose"
      >
        {{ $t('button.close') }}
      </b-button>
      <n-button-loading
        :loading="loadingSubmit"
        class="btn"
        variant="primary"
        :disabled="disableSubmit"
        @submit="handleOk"
      >
        {{ $t('button.confirm') }}
      </n-button-loading>
    </template>
    <table class="table table-bordered">
      <thead>
        <tr>
          <th
            class="text-center"
          >
            <b-form-checkbox
              v-model="selectAll"
              class="checkbox-custom"
              @change="toggleSelectAll"
            />
          </th>
          <th>{{ $t('label.no') }}</th>
          <th>{{ $t('field.serviceName') }}</th>
          <th>{{ $t('field.status') }}</th>
          <th>{{ $t('field.operator') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="items.length === 0">
          <td
            colspan="6"
            class="text-center"
          >
            <b-spinner
              v-if="loading"
              label="Spinning"
            />
            <p v-else>
              {{ $t('general.noRecord') }}
            </p>
          </td>
        </tr>
        <template v-else>
          <tr
            v-for="(item, index) in items"
            :key="item.id"
          >
            <td class="text-center">
              <b-form-checkbox
                v-model="item.selected"
                @change="onItemSelect"
              />
            </td>
            <td class="text-center">
              {{ index+1 }}
            </td>
            <td>
              {{ item.shortenName }}
            </td>
            <td>
              <b-badge
                pill
                badge-glow
                :variant="getStatusVariant(item.status)"
                :style="item.status === 'processing' ? {
                  background: '#007bff',
                  color: '#fff'
                } : {}"
              >
                {{ $t(`status.${item.status === 'resolved' ? 'closed' : (item.status || 'unknown')}`) }}
              </b-badge>
            </td>
            <td>
              <n-async-single-select
                name="userId"
                repository="operator"
                selection-key="id"
                selection-label="name"
                clearable
                reduce
                :readonly="!item.selected"
                :query="{
                  serviceType: item.serviceType.code,
                  serviceCategory: item.serviceCategory.code,
                }"
                :always-excluded="userData.id"
                exclude-key="id"
                :excludes="[userData.id]"
                :calculate-position="withPopper"
                :immediate="false"
                @input="e => onChangeOperator(e, item.id)"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <div
      v-if="items.length > 0"
      class="info-text"
    >
      <feather-icon
        icon="InfoIcon"
        size="18"
      />
      {{ $t('general.handoverDescription') }}
    </div>

    <HandoverOperatorModal
      ref="openHandover"
      :active-channel="activeChannel"
    />
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BBadge, BFormGroup, BSpinner, BFormCheckbox,
} from 'bootstrap-vue'
import NInput from '@/components/NInput.vue'
import useJwt from '@/auth/jwt/useJwt'
import NButtonLoading from '@/components/NButtonLoading.vue'
import Repository from '@/repositories/RepositoryFactory'
import NTable from '@/components/NTable.vue'
import NPagination from '@/components/NPagination.vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import { createPopper } from '@popperjs/core'
import department from '@/router/routes/department'
import ToastificationAvatar from '@/@core/components/toastification/ToastificationAvatar.vue'
import FormInput from './assignOperatorFormInput'
import HandoverOperatorModal from '../HandoverOperatorModal.vue'
import tableFields from './handoverServiceSystemTableField'

const ServiceSystemRepository = Repository.get('serviceSystem')

export default {
  components: {
    BModal,
    BBadge,
    NInput,
    NTable,
    BButton,
    NButtonLoading,
    HandoverOperatorModal,
    NPagination,
    NAsyncSingleSelect,
    BFormGroup,
    BSpinner,
    BFormCheckbox,
  },
  props: {
    activeChannel: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      dataModel: {
        userId: '',
      },
      items: [],
      key: 1,
      perPage: 10,
      loadingTable: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isActive: this.$route.query.isActive || null,
      },
      loading: false,
      loadingSubmit: false,
      disableSubmit: true,
      selectAll: false,
    }
  },

  computed: {
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone  
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    perPageComputed: {
      get() {
        return this.perPage
      },
      set(value) {
        this.perPage = value
      },
    },
  },

  watch: {
    items: {
      handler() {
        this.disableSubmit = !this.items.length || !this.items.every(item => item.userId)
      },
      deep: true,
    },
    perPageComputed() {
      this.listFile()
    },
  },
  methods: {
    confirmDestroy(id, index, data) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: `#${index + 1} ${data.shortenName}`,
          }),
          {
            title: this.$t('alert.confirmContinueOperation'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.yesDelete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.destroy(id)
          }
        })
    },
    destroy(id) {
      this.items = this.items.filter(item => item.id !== id)
    },
    withPopper(dropdownList, component, { width }) {
      dropdownList.style.width = width
      dropdownList.style.zIndex = 9999999

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      })
      return () => popper.destroy()
    },
    onChangeOperator(operatorId, serviceSystemId) {
      const index = this.items.findIndex(item => item.id === serviceSystemId)
      if (index !== -1) {
        this.items[index].userId = operatorId
      }

      this.disableSubmit = !this.items.some(item => item.selected)
        || !this.items.filter(item => item.selected).every(item => item.userId)
    },
    submit() {
      this.loadingSubmit = true
      const payload = this.items
        .filter(item => item.selected)
        .map(item => ({
          serviceSystemId: item.id,
          operatorId: item.userId,
        }))
      ServiceSystemRepository.handover({
        handovers: payload,
      })
        .then(() => {
          this.hide()
          this.$toast({
            component: ToastificationAvatar,
            position: 'top-right',
            timeout: 10000,
            props: {
              title: this.$t('button.handover'),
              icon: 'CastIcon',
              variant: 'success',
              text: this.$t('alert.handoverSuccessfully'),
            },
          })
          this.$emit('success')
        })
        .catch(error => {
          if (error.response?.status === 422) {
            this.$refs.assignOperatorForm.setErrors(
              error.response?.data?.validate,
            )
          }
        })
        .then(() => {
          this.loadingSubmit = false
        })
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        cancel: 'danger',
        hold: 'primary',
        completed: 'success',
        resolved: 'secondary',
        unresolved: 'danger',
        handover: 'primary',
      }
      return variants[status] || 'secondary'
    },
    list() {
      this.getData()
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    show() {
      this.key += 1
      this.list()
      this.$bvModal.show('handover-service-system-modal')
    },
    getData() {
      this.loading = true
      ServiceSystemRepository.handoverServices({
        ...this.query,
      })
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.items = data.list.map(item => ({
              ...item,
              selected: false,
            }))
            this.total = data.count
          }
        })
        .catch(() => {
          this.loading = false
        })
        .then(() => {
          this.loading = false
        })
    },
    hide() {
      this.$bvModal.hide('handover-service-system-modal')
    },
    handleClose() {
      this.hide()
    },
    handleOk() {
      this.submit()
    },
    toggleSelectAll(checked) {
      this.items.forEach(item => {
        item.selected = checked
      })
      this.onItemSelect()
    },
    onItemSelect() {
      this.selectAll = this.items.length > 0 && this.items.every(item => item.selected)

      this.disableSubmit = !this.items.some(item => item.selected)
        || !this.items.filter(item => item.selected).every(item => item.userId)
    },
  },
  setup() {
    const formInput = [...FormInput]
    const fields = tableFields

    return {
      formInput,
      fields,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.table {
  width: 100%;
  margin-bottom: 1rem;
}
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
}

.info-text {
  display: flex;
  align-items: center;
  justify-items: start;

  svg {
    flex-shrink: 0;
    margin-right: 8px;
  }
}

.table thead th:nth-child(5) {
  width: 300px;
}

// Updated checkbox styles with higher specificity and correct selectors
.checkbox-custom.custom-control {
  .custom-control-input:checked ~ .custom-control-label::before {
    border: 1px solid #ffffff !important;
  }
}
</style>
